// Soft + permanent material deletion for the inventory orchestrator.
// Keeps `ManufacturingInventoryPanel.tsx` under the file-size hard cap.
import { useMaterialDeletionFlow } from "./useMaterialDeletionFlow";
import { useMaterialPermanentDeleteFlow } from "./useMaterialPermanentDeleteFlow";
import { describeDeletionPending } from "./materialDeletionPendingCopy";
import type { MfgMaterial } from "@/lib/manufacturing/types";

type Cleanup = {
  deactivateMaterial: (material: MfgMaterial) => Promise<{ ok: true } | { ok: false; error: string }>;
  restoreMaterial: (material: MfgMaterial) => Promise<{ ok: true } | { ok: false; error: string }>;
  bulkDeactivateMaterials: (
    ids: ReadonlyArray<string>,
  ) => Promise<{ updated: number; failed: number }>;
  bulkRestoreMaterials: (
    ids: ReadonlyArray<string>,
  ) => Promise<{ updated: number; failed: number }>;
  clearMaterialSelection: () => void;
};

type Opts = {
  cleanup: Cleanup;
  refreshMaterials: () => void;
  onSummaryToast: (message: string) => void;
};

export function useManufacturingInventoryDeletions({
  cleanup,
  refreshMaterials,
  onSummaryToast,
}: Opts) {
  const deletionFlow = useMaterialDeletionFlow({
    deactivateMaterial: cleanup.deactivateMaterial,
    restoreMaterial: cleanup.restoreMaterial,
    bulkDeactivateMaterials: cleanup.bulkDeactivateMaterials,
    bulkRestoreMaterials: cleanup.bulkRestoreMaterials,
    onSummaryToast,
    onBulkApplied: cleanup.clearMaterialSelection,
  });
  const permanentDeleteFlow = useMaterialPermanentDeleteFlow({
    onSummaryToast,
    onDeleted: () => {
      cleanup.clearMaterialSelection();
      refreshMaterials();
    },
  });

  return {
    pendingCopy: describeDeletionPending(deletionFlow.pending),
    requestSingleDelete: deletionFlow.requestSingleDelete,
    requestSingleRestore: deletionFlow.requestSingleRestore,
    requestBulkDelete: deletionFlow.requestBulkDelete,
    requestBulkRestore: deletionFlow.requestBulkRestore,
    requestPermanentDelete: permanentDeleteFlow.request,
    softModal: {
      deletionBusy: deletionFlow.pending?.busy ?? false,
      onDeletionCancel: deletionFlow.cancel,
      onDeletionConfirm: () => void deletionFlow.confirm(),
    },
    permanentModal: {
      permanentDeleteMaterial: permanentDeleteFlow.pending?.material ?? null,
      permanentDeleteBusy: permanentDeleteFlow.pending?.busy ?? false,
      permanentDeleteError: permanentDeleteFlow.pending?.error ?? null,
      permanentDeleteBlockers: permanentDeleteFlow.pending?.blockers ?? [],
      onPermanentDeleteCancel: permanentDeleteFlow.cancel,
      onPermanentDeleteConfirm: () => void permanentDeleteFlow.confirm(),
    },
  };
}
